/**
 * Стадия заказа → что видит пользователь и что он может сделать (FE-200).
 *
 * Чистая таблица, а не логика внутри карточки: соответствие «стадия → подпись,
 * тон, главное действие» — продуктовое решение из MVP_PRD §33.2, и его нужно
 * уметь прочитать целиком и проверить тестом.
 *
 * **Одно главное действие на карточку** (DESIGN_SYSTEM §5.1). «Открыть» для
 * всех означало бы вести пользователя из `draft` на пустой экран ожидания.
 *
 * **Кнопка ведёт только на существующий экран.** Экраны анализа (FE-221),
 * оплаты (FE-210), четвёрки (FE-220) и сопровождения (FE-260) уже есть;
 * контур осмотра приходит с Epic 41, и до него его стадии открывают четвёрку —
 * последний экран, на котором заказу есть что показать. Кнопка, обещающая
 * ненаписанный экран, — это обещание отсутствующего функционала (MVP_PRD §2), а
 * отключённая кнопка без причины — дефект (DESIGN_SYSTEM §5.9). Когда экран
 * появляется, правится одна строка таблицы.
 */

import type { OrderStage } from '@entities/search-session';
import { ROUTES } from '@shared/config/routes';
import type { BadgeTone } from '@shared/ui';

/** Куда ведёт основная кнопка карточки. */
export type OrderAction =
  'continue' | 'run' | 'progress' | 'results' | 'refresh' | 'retry' | 'open' | 'restore';

/** Какой экран открывает главное действие. Все — уже существующие маршруты. */
type StageScreen = 'assistant' | 'analysis' | 'results' | 'top4' | 'payment' | 'handover';

export interface OrderStageView {
  /** Ключ i18n подписи стадии (`searches.stage.*`). */
  labelKey: string;
  tone: BadgeTone;
  /** Ключ i18n основного действия (`searches.action.*`). */
  actionKey: string;
  action: OrderAction;
  screen: StageScreen;
}

const VIEWS: Record<OrderStage, OrderStageView> = {
  draft: {
    labelKey: 'searches.stage.draft',
    tone: 'neutral',
    actionKey: 'searches.action.continue',
    action: 'continue',
    screen: 'assistant',
  },
  ready: {
    labelKey: 'searches.stage.ready',
    tone: 'info',
    actionKey: 'searches.action.run',
    action: 'run',
    screen: 'assistant',
  },
  analyzing: {
    labelKey: 'searches.stage.analyzing',
    tone: 'action',
    actionKey: 'searches.action.progress',
    action: 'progress',
    screen: 'analysis',
  },
  analysis_failed: {
    labelKey: 'searches.stage.analysis_failed',
    tone: 'attention',
    actionKey: 'searches.action.retry',
    action: 'retry',
    screen: 'analysis',
  },
  shortlist_ready: {
    labelKey: 'searches.stage.shortlist_ready',
    tone: 'match',
    actionKey: 'searches.action.results',
    action: 'results',
    screen: 'results',
  },
  awaiting_registration: {
    labelKey: 'searches.stage.awaiting_registration',
    tone: 'info',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'payment',
  },
  awaiting_payment: {
    labelKey: 'searches.stage.awaiting_payment',
    tone: 'info',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'payment',
  },
  paid: {
    labelKey: 'searches.stage.paid',
    tone: 'match',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'top4',
  },
  selecting: {
    labelKey: 'searches.stage.selecting',
    tone: 'action',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'top4',
  },
  top4_ready: {
    labelKey: 'searches.stage.top4_ready',
    tone: 'match',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'top4',
  },
  checking: {
    labelKey: 'searches.stage.checking',
    tone: 'action',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'top4',
  },
  comparison_ready: {
    labelKey: 'searches.stage.comparison_ready',
    tone: 'match',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'top4',
  },
  deep_check: {
    labelKey: 'searches.stage.deep_check',
    tone: 'action',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'top4',
  },
  deep_check_ready: {
    labelKey: 'searches.stage.deep_check_ready',
    // Ждёт решения пользователя: ответы собраны, надо выбрать, с кем
    // торговаться. Тон внимания, а не действия — заказ стоит на месте.
    tone: 'attention',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'top4',
  },
  negotiating: {
    labelKey: 'searches.stage.negotiating',
    tone: 'action',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'top4',
  },
  offer_review: {
    labelKey: 'searches.stage.offer_review',
    tone: 'attention',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'top4',
  },
  conclusion_ready: {
    labelKey: 'searches.stage.conclusion_ready',
    tone: 'compromise',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'top4',
  },
  // Контур осмотра (Epic 41). Стадии объявлены вместе с `deep_check_ready`
  // одной миграцией (BE-402), собственных экранов у них пока нет — карточка
  // ведёт на четвёрку, как и остальные стадии без своего экрана. Когда экран
  // осмотра появится (FE-410), правятся три строки этой таблицы.
  inspection: {
    labelKey: 'searches.stage.inspection',
    tone: 'action',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'top4',
  },
  inspection_review: {
    labelKey: 'searches.stage.inspection_review',
    tone: 'attention',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'top4',
  },
  renegotiating: {
    labelKey: 'searches.stage.renegotiating',
    tone: 'action',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'top4',
  },
  handover: {
    labelKey: 'searches.stage.handover',
    tone: 'match',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'handover',
  },
  // Заказ завершён отметкой исхода: экран сопровождения остаётся доступен на
  // чтение (§39.3). Вести завершённый заказ на четвёрку значило бы предлагать
  // выбрать автомобиль там, где выбор уже сделан.
  completed: {
    labelKey: 'searches.stage.completed',
    tone: 'neutral',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'handover',
  },
  closed: {
    labelKey: 'searches.stage.closed',
    tone: 'neutral',
    actionKey: 'searches.action.open',
    action: 'open',
    screen: 'results',
  },
  stale: {
    labelKey: 'searches.stage.stale',
    tone: 'attention',
    actionKey: 'searches.action.refresh',
    action: 'refresh',
    screen: 'analysis',
  },
  archived: {
    labelKey: 'searches.stage.archived',
    tone: 'neutral',
    actionKey: 'searches.action.restore',
    action: 'restore',
    screen: 'results',
  },
};

export function orderStageView(stage: OrderStage): OrderStageView {
  return VIEWS[stage];
}

/** Адрес экрана, на который ведёт главное действие карточки. */
const SCREEN_ROUTE: Record<StageScreen, (searchId: string) => string> = {
  assistant: ROUTES.searchAssistant,
  analysis: ROUTES.searchAnalysis,
  results: ROUTES.searchResults,
  top4: ROUTES.searchTop4,
  payment: ROUTES.searchPayment,
  handover: ROUTES.searchHandover,
};

export function stageRoute(stage: OrderStage, searchId: string): string {
  return SCREEN_ROUTE[VIEWS[stage].screen](searchId);
}

/**
 * Сколько переговоров ждут решения пользователя (MVP_PRD §33.3).
 *
 * Считается из счётчиков по статусам, а не отдельным полем ответа: два числа об
 * одном и том же расходятся ровно тогда, когда их читают в разных местах.
 */
export function awaitingDecisions(counts: Record<string, number>): number {
  return counts['awaiting_user'] ?? 0;
}
